import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '../lib/apiFetch';
import { queryKeys } from '../lib/queryClient';

// Variations are embedded in each product group so the picker can render
// expand-to-variations synchronously. When the catalog was fetched with a
// rental window, each variation also carries window-aware `available`/`total`.
export interface CatalogVariation {
  id: string;
  name: string;
  sku: string;
  base_price_in_cents: number;
  stock_count: number;
  available?: number;
  total?: number;
}

export interface CatalogProductGroup {
  id: string;
  name: string;
  sku: string;
  description?: string;
  photo_url?: string;
  trackable: boolean;
  variation: boolean;
  has_variations: boolean;
  base_price_in_cents: number;
  stock_count: number;
  product_type?: string;
  show_in_store?: boolean;
  variations: CatalogVariation[];
}

interface CatalogResponse {
  product_groups: CatalogProductGroup[];
  item_stock: Record<string, number>;
  availability_window: { from: string; till: string } | null;
}

interface UseProductCatalogOptions {
  fromDate?: string;
  tillDate?: string;
}

const fetchCatalog = async (
  fromDate?: string,
  tillDate?: string
): Promise<CatalogResponse> => {
  const params = new URLSearchParams();
  // Admin catalog/picker shows the full catalog — services and hidden sale
  // items (show_in_store=false) included, matching the Services/SaleItems
  // admin pages. The storefront (website) uses a different path and omits this.
  params.set('include_hidden', 'true');
  if (fromDate) params.set('from', fromDate);
  if (tillDate) params.set('till', tillDate);
  const qs = params.toString();
  const url = `/booqable/product-groups${qs ? `?${qs}` : ''}`;
  const res = await apiFetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch catalog: ${res.statusText}`);
  }
  const data = await res.json();
  return {
    product_groups: data.product_groups || [],
    item_stock: data.item_stock || {},
    availability_window: data.availability_window || null,
  };
};

// Single source of truth for product catalog — both the picker and the
// order-page qty caps read from this hook so the data is fetched once per
// (window, stale-window) and shared across every component within that
// window. When `fromDate`/`tillDate` are provided, embedded variations also
// carry per-window availability so the picker renders without round trips
// on expand.
export function useProductCatalog(opts: UseProductCatalogOptions = {}) {
  const { fromDate, tillDate } = opts;
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: [...queryKeys.inventory.all, 'catalog', fromDate ?? null, tillDate ?? null],
    queryFn: () => fetchCatalog(fromDate, tillDate),
  });

  return {
    productGroups: data?.product_groups ?? [],
    stockMap: data?.item_stock ?? {},
    isLoading,
    isFetching,
    error,
  };
}
